import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import axios from 'axios';

import Login from './pages/Login';
import Dashboard from './pages/DashboardKPI';
import Productivity from './pages/Productivity';
import HRManagement from './pages/HRManagement';
import Employees from './pages/Employees';
import Drivers from './pages/Drivers';
import DriverRegister from './pages/DriverRegister';
import DailyEntryForm from './pages/DailyEntryForm';
import DailyEntriesHistory from './pages/DailyEntriesHistory';
import ClientsAdmin from './pages/ClientsAdmin';
import Messages from './pages/Messages';
import Incidents from './pages/Incidents';
import Documents from './pages/Documents';
import Payslips from './pages/Payslips';
import AdminDocuments from './pages/AdminDocuments';
import AdminMessaging from './pages/AdminMessaging';
import AdminKPIs from './pages/AdminKPIs';
import AdminDriverForm from './pages/AdminDriverForm';

// Lucide-style icon paths (stroke, 24x24)
const ICONS = {
  dashboard: ['M3 3h7v7H3z', 'M14 3h7v7h-7z', 'M14 14h7v7h-7z', 'M3 14h7v7H3z'],
  edit: ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z'],
  history: ['M3 3v5h5', 'M3.05 13A9 9 0 1 0 6 5.3L3 8', 'M12 7v5l4 2'],
  message: ['M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z'],
  alert: ['M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z', 'M12 9v4', 'M12 17h.01'],
  file: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6', 'M16 13H8', 'M16 17H8'],
  receipt: ['M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1z', 'M8 7h8', 'M8 11h6'],
  chart: ['M3 3v18h18', 'M18 17V9', 'M13 17V5', 'M8 17v-3'],
  send: ['M22 2 11 13', 'M22 2 15 22l-4-9-9-4z'],
  users: ['M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2', 'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8', 'M22 21v-2a4 4 0 0 0-3-3.87', 'M16 3.13a4 4 0 0 1 0 7.75'],
  truck: ['M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2', 'M15 18H9', 'M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.62l-3.48-4.35A1 1 0 0 0 17.52 8H14', 'M8 18a2 2 0 1 0 4 0 2 2 0 0 0-4 0z', 'M16 18a2 2 0 1 0 4 0 2 2 0 0 0-4 0z'],
  settings: ['M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z', 'M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z'],
};

function NavIcon({ paths }) {
  return (
    <svg className="side-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths.map((d, i) => <path key={i} d={d} />)}
    </svg>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (token && userData) {
      setUser(JSON.parse(userData));
      fetchNotifications();
    }
    setLoading(false);
  }, []);

  // Charger les notifications
  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const [countResponse, listResponse] = await Promise.all([
        axios.get('/api/notifications/unread-count', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get('/api/notifications?limit=5', {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setUnreadNotifications(countResponse.data.count);
      setNotifications(listResponse.data);
    } catch (error) {
      console.error('Erreur notifications:', error);
    }
  };

  // Rafraîchir les notifications toutes les 30 secondes
  useEffect(() => {
    if (user) {
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // Cartes empilées en mobile : injecte data-label sur chaque cellule depuis l'entête de colonne
  useEffect(() => {
    const labelTables = () => {
      document.querySelectorAll('table').forEach((table) => {
        const heads = Array.from(table.querySelectorAll('thead th')).map((th) => th.textContent.trim());
        if (!heads.length) return;
        table.querySelectorAll('tbody tr').forEach((tr) => {
          Array.from(tr.children).forEach((td, i) => {
            if (heads[i] && td.getAttribute('data-label') !== heads[i]) {
              td.setAttribute('data-label', heads[i]);
            }
          });
        });
      });
    };
    labelTables();
    const obs = new MutationObserver(labelTables);
    obs.observe(document.body, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, []);

  const handleMarkAsRead = async (notifId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/notifications/${notifId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNotifications();
    } catch (error) {
      console.error('Erreur mark as read:', error);
    }
  };

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  if (loading) {
    return <div className="loading">Chargement…</div>;
  }

  const navItems = !user ? [] : (user.role === 'driver' ? [
    { href: '/dashboard', label: 'Tableau de bord', icon: ICONS.dashboard },
    { href: '/daily-entry', label: 'Saisie quotidienne', icon: ICONS.edit },
    { href: '/history', label: 'Historique', icon: ICONS.history },
    { href: '/messages', label: 'Messages', icon: ICONS.message },
    { href: '/incidents', label: 'Incidents', icon: ICONS.alert },
    { href: '/documents', label: 'Documents', icon: ICONS.file },
    { href: '/payslips', label: 'Bulletins', icon: ICONS.receipt },
  ] : [
    { href: '/dashboard', label: 'Tableau de bord', icon: ICONS.dashboard },
    { href: '/productivity', label: 'Productivité', icon: ICONS.chart },
    { href: '/history', label: 'Saisies', icon: ICONS.history },
    { href: '/messages', label: 'Messages', icon: ICONS.message },
    { href: '/incidents', label: 'Incidents', icon: ICONS.alert },
    { href: '/admin/documents', label: 'Documents', icon: ICONS.file },
    { href: '/admin/messages-chauffeurs', label: 'Messagerie', icon: ICONS.send },
    { href: '/admin/kpis', label: 'Saisie KPIs', icon: ICONS.edit },
    { href: '/hr', label: 'Gestion RH', icon: ICONS.users },
    { href: '/drivers', label: 'Chauffeurs', icon: ICONS.truck },
    ...(user.role === 'admin' ? [{ href: '/clients', label: 'Paramètres', icon: ICONS.settings }] : []),
  ]);

  const currentPath = window.location.pathname;
  const isActive = (href) => currentPath === href || (href !== '/dashboard' && currentPath.startsWith(href));

  const routes = (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <Login onLogin={handleLogin} />} />
      <Route path="/register" element={!user ? <DriverRegister /> : <Navigate to="/dashboard" />} />
      <Route path="/dashboard" element={user ? <Dashboard user={user} /> : <Navigate to="/login" />} />
      <Route path="/daily-entry" element={user && user.role === 'driver' ? <DailyEntryForm user={user} /> : <Navigate to="/dashboard" />} />
      <Route path="/history" element={user ? <DailyEntriesHistory user={user} /> : <Navigate to="/login" />} />
      <Route path="/productivity" element={user ? <Productivity user={user} /> : <Navigate to="/login" />} />
      <Route path="/hr" element={user && (user.role === 'admin' || user.role === 'manager') ? <HRManagement user={user} /> : <Navigate to="/dashboard" />} />
      <Route path="/employees" element={user && (user.role === 'admin' || user.role === 'manager') ? <Employees user={user} /> : <Navigate to="/dashboard" />} />
      <Route path="/drivers" element={user && (user.role === 'admin' || user.role === 'manager') ? <Drivers user={user} /> : <Navigate to="/dashboard" />} />
      <Route path="/clients" element={user && user.role === 'admin' ? <ClientsAdmin /> : <Navigate to="/dashboard" />} />
      <Route path="/messages" element={user ? <Messages user={user} /> : <Navigate to="/login" />} />
      <Route path="/incidents" element={user ? <Incidents user={user} /> : <Navigate to="/login" />} />
      <Route path="/documents" element={user ? <Documents user={user} /> : <Navigate to="/login" />} />
      <Route path="/payslips" element={user && user.role === 'driver' ? <Payslips user={user} /> : <Navigate to="/dashboard" />} />
      <Route path="/admin/documents" element={user && (user.role === 'admin' || user.role === 'manager') ? <AdminDocuments user={user} /> : <Navigate to="/dashboard" />} />
      <Route path="/admin/messages-chauffeurs" element={user && user.role === 'admin' ? <AdminMessaging user={user} /> : <Navigate to="/dashboard" />} />
      <Route path="/admin/kpis" element={user && (user.role === 'admin' || user.role === 'manager') ? <AdminKPIs user={user} /> : <Navigate to="/dashboard" />} />
      <Route path="/drivers/new" element={user && (user.role === 'admin' || user.role === 'manager') ? <AdminDriverForm user={user} /> : <Navigate to="/dashboard" />} />
      <Route path="/drivers/edit/:id" element={user && (user.role === 'admin' || user.role === 'manager') ? <AdminDriverForm user={user} /> : <Navigate to="/dashboard" />} />
      <Route path="/" element={<Navigate to={user ? "/dashboard" : "/login"} />} />
    </Routes>
  );

  return (
    <Router>
      <div className={`App ${user ? 'app-shell' : ''}`}>
        {user ? (
          <>
            <div className={`mobile-overlay ${mobileMenuOpen ? 'open' : ''}`} onClick={() => setMobileMenuOpen(false)}></div>

            <aside className={`sidebar ${mobileMenuOpen ? 'open' : ''}`}>
              <div className="sidebar-brand">
                <img src="/logo.png" alt="RZ Transport — Road Zenith" />
                <span>RZ Board</span>
                <button className="sidebar-close" onClick={() => setMobileMenuOpen(false)} aria-label="Fermer le menu">×</button>
              </div>

              <nav className="sidebar-nav">
                {navItems.map((item) => (
                  <a key={item.href} href={item.href}
                    className={`side-link ${isActive(item.href) ? 'active' : ''}`}
                    onClick={() => setMobileMenuOpen(false)}>
                    <NavIcon paths={item.icon} />
                    <span>{item.label}</span>
                  </a>
                ))}
              </nav>

              <div className="sidebar-footer">
                <div className="sidebar-user">
                  {user.role === 'driver' && user.driver ? (
                    <>
                      <span className="su-name">{user.driver.firstname} {user.driver.lastname}</span>
                      <span className="su-meta">
                        {user.driver.vehicle_plate}
                        {user.driver.vehicle_type && ` · ${user.driver.vehicle_type}`}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="su-name">{user.email}</span>
                      <span className="su-meta">{user.role}</span>
                    </>
                  )}
                </div>
                <button onClick={handleLogout} className="btn-logout side-logout">Déconnexion</button>
              </div>
            </aside>

            <div className="app-main">
              <header className="topbar">
                <button className="hamburger-menu" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Ouvrir le menu">
                  <span></span><span></span><span></span>
                </button>
                <div className="topbar-spacer"></div>

                <div className="topbar-actions">
                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={() => setShowNotifications(!showNotifications)}
                      className="topbar-iconbtn"
                      aria-label="Notifications"
                    >
                      <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                      </svg>
                      {unreadNotifications > 0 && (
                        <span className="notif-badge">{unreadNotifications > 9 ? '9+' : unreadNotifications}</span>
                      )}
                    </button>

                    {showNotifications && (
                      <div className="notif-dropdown">
                        <div className="notif-head">
                          <span>Notifications</span>
                          {unreadNotifications > 0 && <span className="notif-count">{unreadNotifications}</span>}
                        </div>
                        {notifications.length === 0 ? (
                          <div className="notif-empty">Aucune notification</div>
                        ) : (
                          <div>
                            {notifications.map(notif => (
                              <div
                                key={notif.id}
                                className={`notif-item ${notif.is_read ? '' : 'unread'}`}
                                onClick={() => {
                                  handleMarkAsRead(notif.id);
                                  if (notif.link) window.location.href = notif.link;
                                  setShowNotifications(false);
                                }}
                              >
                                <div className="notif-title">{notif.title}</div>
                                <div className="notif-msg">{notif.message}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </header>

              {routes}
            </div>
          </>
        ) : routes}
      </div>
    </Router>
  );
}

export default App;
